import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'fz-filters',
  standalone: true,
  imports: [MatIcon, MatButton, MatIconButton, FormsModule, MatBadgeModule],
  templateUrl: './filters.html',
  styleUrl: './filters.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'fz-filters',
    '[class.fz-filters--search-expanded]': 'isSearchExpanded()',
    '[class.fz-filters--badge]': '!isSearchExpanded() && searchTerm()',
  },
})
export class Filters {
  readonly #fb = inject(FormBuilder);

  /*
   * Inputs and Outputs
   */
  searchPlaceholder = input<string>('Search...');
  initialSearchTerms = input<string | undefined>('');
  disableAdvanceFilters = input<boolean>(false);
  activeFilterCount = input<number>(0);
  opened = output<void>();
  closed = output<void>();
  filterPanel = output<boolean>();
  searchChange = output<string>();

  /*
   * Local state
   */
  isSearchExpanded = signal(false);
  searchTerm = signal('');

  searchForm = this.#fb.group({
    search: [],
  });

  constructor() {
    effect(() => {
      const searchTerm = this.searchTerm();

      if (searchTerm) {
        this.emitChange();
      }
    });
  }

  /**
   * Prevents event propagation and default behavior.
   *
   * @param event - The event to prevent propagation and default behavior for
   * @private
   */
  private preventEventPropagation(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  @delaySearch(1000)
  private emitChange() {
    this.searchChange.emit(this.searchTerm());
  }

  expandSearch(event: MouseEvent) {
    this.preventEventPropagation(event);
    this.isSearchExpanded.set(true);
    this.opened.emit();
  }

  collapseSearch(event: MouseEvent) {
    this.preventEventPropagation(event);
    this.isSearchExpanded.set(false);
    this.closed.emit();
  }

  clearSearch() {
    this.searchChange.emit('');
  }

  formSubmit(event: SubmitEvent) {
    this.preventEventPropagation(event);

    const formData = new FormData(event.target as HTMLFormElement);
    const search = formData.get('search');

    if (search) {
      this.searchChange.emit(search as string);
    }
  }

  toggleFilterPanel(event: Event) {
    event.preventDefault();
    this.filterPanel.emit(true);
  }
}

/**
 * Creates a decorator that delays the execution of a method
 * @param delay Time in milliseconds to delay the function execution
 */
function delaySearch(delay: number = 100) {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor,
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        originalMethod.apply(this, args);
      }, delay);
    };

    return descriptor;
  };
}
